import { z } from 'zod';

/**
 * Messages réseau discrets de Panda Panic en ligne (Phase 4, transport Colyseus,
 * spec 15 §6). L'état autoritaire (positions, tuiles, résultat) est synchronisé
 * par le state-schema de la salle, PAS par ces messages : ceux-ci ne portent que
 * des intentions (client → serveur) et des signaux hors-état (serveur → client).
 * Rien de sensible n'y transite (ADR 0003).
 */

/** Types de messages émis par le client vers la salle. */
export const PandaPanicClientMessages = {
  /** Entrées du joueur pour un tick — appliquées par le serveur autoritaire. */
  Input: 'pp:input',
  /** L'hôte lance la partie une fois le quorum atteint. */
  Start: 'pp:start',
  /** Rejouer avec les joueurs présents. */
  Rematch: 'pp:rematch',
} as const;
export type PandaPanicClientMessage =
  (typeof PandaPanicClientMessages)[keyof typeof PandaPanicClientMessages];

/** Types de messages émis par la salle vers un client. */
export const PandaPanicServerMessages = {
  /** Siège attribué à ce client au join (le reste passe par le state-schema). */
  Welcome: 'pp:welcome',
  /** Erreur applicative (code stable + libellé court). */
  Error: 'pp:error',
} as const;
export type PandaPanicServerMessage =
  (typeof PandaPanicServerMessages)[keyof typeof PandaPanicServerMessages];

/**
 * Charge du message de bienvenue : le serveur indique au client SON siège (0..
 * maxPlayers-1) dès le join. Le client s'en sert pour se situer (caméra, HUD
 * J1/J2) ; l'état de jeu, lui, arrive par le state-schema. Aucune donnée cachée.
 */
export const PandaPanicWelcomeSchema = z.object({
  seat: z.number().int().nonnegative(),
});
export type PandaPanicWelcome = z.infer<typeof PandaPanicWelcomeSchema>;

/**
 * Entrées d'un joueur pour un tick. Mappe `InputState` (game-logic core) : le
 * serveur borne la magnitude de `move` (‖move‖ ≤ 1) et ignore les `seq`
 * obsolètes. Les composantes sont validées finies et dans [-1, 1] — première
 * barrière contre les valeurs aberrantes d'un client hostile.
 */
export const PandaPanicInputSchema = z.object({
  seq: z.number().int().nonnegative(),
  move: z.object({
    x: z.number().finite().min(-1).max(1),
    y: z.number().finite().min(-1).max(1),
  }),
  action: z.boolean(),
});
export type PandaPanicInput = z.infer<typeof PandaPanicInputSchema>;

/** Charge d'un message d'erreur serveur → client. */
export const PandaPanicErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string(),
});
export type PandaPanicError = z.infer<typeof PandaPanicErrorSchema>;
